import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import AutoSeo from "@/components/AutoSeo";
import PageMeta from "@/components/PageMeta";
import { groupFaqs, faqCatId, faqRich, faqPlain } from "@/lib/faqCategories";
import FaqMedia from "@/components/FaqMedia";

// FAQPage JSON-LD — emit ONCE per FAQ list. Eligible for the dropdown-style
// FAQ rich result in Google search (each question expandable inline).
function injectFaqSchema(faqs) {
  if (!Array.isArray(faqs) || faqs.length === 0) return undefined;
  const id = "rf-faq-schema";
  let s = document.getElementById(id);
  if (!s) {
    s = document.createElement("script");
    s.id = id;
    s.type = "application/ld+json";
    document.head.appendChild(s);
  }
  s.text = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.slice(0, 200).map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: faqPlain(f.answer) },
    })),
  });
  return () => { const el = document.getElementById(id); if (el) el.remove(); };
}

export default function FAQs() {
  const [faqs, setFaqs] = useState([]);
  const [q, setQ] = useState("");
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
    api.get("/public/site-content/faq").then((r) => setContent(r.data.content)).catch(() => {});
  }, []);

  useEffect(() => injectFaqSchema(faqs), [faqs]);

  const [free, setFree] = useState("5");
  useEffect(() => { api.get("/settings").then((r) => setFree(String(r.data?.free_recipe_limit || 5))).catch(() => {}); }, []);
  const t = (s) => (s || "").replace(/\{FREE\}/g, free);
  const filtered = faqs.filter(
    (f) => !q || f.question.toLowerCase().includes(q.toLowerCase()) || f.answer.toLowerCase().includes(q.toLowerCase())
  );

  const groups = groupFaqs(filtered);

  if (!content) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="faq-loading">
        <div className="w-8 h-8 border-2 border-rf-orange border-t-transparent rounded-full animate-spin" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-20">
      <AutoSeo
        pageKey="faq"
        fallbackTitle="Frequently Asked Questions"
        fallbackDescription="Recipe Filer FAQs — how scanning handwritten recipes works, sharing with family, printing hardback cookbooks, free tier limits, subscriptions and more."
      />
      <p className="overline mb-4">{content.overline}</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-6 leading-[0.95]">{content.h1}</h1>
      <p className="text-rf-ink-soft mb-10">{content.intro}</p>

      <input
        data-testid="faq-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search FAQs…"
        className="input-rf mb-10"
      />

      {/* Jump-to-heading chips */}
      <nav aria-label="FAQ sections" className="flex flex-wrap gap-2 mb-10 mobile-scroll-row" data-testid="faq-heading-nav">
        {groups.map(([cat, items]) => (
          <a key={cat} href={`#${faqCatId(cat)}`} data-testid={`faq-jump-${faqCatId(cat)}`}
            className="chip !py-2 !px-3 hover:!bg-rf-amber transition-colors">
            {cat} <span className="ml-1 text-rf-ink-soft">{items.length}</span>
          </a>
        ))}
      </nav>

      <div className="space-y-12">
        {groups.map(([cat, items]) => (
          <section key={cat} id={faqCatId(cat)} className="scroll-mt-24" data-testid={`faq-section-${faqCatId(cat)}`}>
            <h2 className="font-display text-2xl md:text-3xl font-black text-rf-ink mb-4">{cat}</h2>
            <Accordion type="multiple" className="space-y-2">
              {items.map((f) => (
                <AccordionItem key={f.faq_id || f.slug} value={f.faq_id || f.slug} className="border border-rf-ink/10 rounded-2xl bg-white px-5">
                  <AccordionTrigger data-testid={`faq-${f._also ? "also-" : ""}${f.faq_id || f.slug}`} className="text-left font-display font-bold text-base md:text-lg text-rf-ink hover:no-underline">
                    {t(f.question)}
                  </AccordionTrigger>
                  <AccordionContent className="text-rf-ink-soft leading-relaxed">
                    <div className="whitespace-pre-line">{faqRich(t(f.answer))}</div>
                    <FaqMedia media={f.media} alt={t(f.question)} testid={`faq-media-${f.faq_id || f.slug}`} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        ))}
        {filtered.length === 0 && <p className="text-rf-ink-soft text-center py-10">No FAQ matches your search.</p>}
      </div>

      {(content.contact_label || content.contact_email) && (
        <div className="mt-12 card-recipe text-center" data-testid="faq-contact-card">
          <p className="font-display font-extrabold text-rf-ink text-lg mb-1">{content.contact_label}</p>
          <p className="text-rf-ink-soft text-sm">
            {content.contact_body}{" "}
            <a href={`mailto:${content.contact_email}`} className="text-rf-orange font-bold underline">{content.contact_email}</a>
          </p>
        </div>
      )}
    </div>
  );
}
